import React, { useEffect, useState } from 'react';
import { Node } from 'reactflow';
import { X, Plus, Trash2, Info } from 'lucide-react';
import { useWorkflowStore } from '../../store/workflow.store';

interface Props {
  node: Node;
  onClose: () => void;
}

export function NodeProperties({ node, onClose }: Props) {
  const { updateNodeData } = useWorkflowStore();
  const [label, setLabel] = useState(node.data.label || '');
  const [description, setDescription] = useState((node.data.description as string) || '');
  const [integration, setIntegration] = useState((node.data.integration as string) || '');
  const [action, setAction] = useState((node.data.action as string) || '');
  const [timeout, setTimeout_] = useState((node.data.timeout as number) || 60);
  const [params, setParams] = useState<{ key: string; value: string }[]>(() => {
    const p = node.data.params as Record<string, unknown> || {};
    return Object.entries(p).map(([key, value]) => ({ key, value: String(value) }));
  });

  useEffect(() => {
    setLabel(node.data.label || '');
    setDescription((node.data.description as string) || '');
    setIntegration((node.data.integration as string) || '');
    setAction((node.data.action as string) || '');
    setTimeout_((node.data.timeout as number) || 60);
    const p = node.data.params as Record<string, unknown> || {};
    setParams(Object.entries(p).map(([key, value]) => ({ key, value: String(value) })));
  }, [node.id]);

  const handleSave = () => {
    const paramsObj = params.reduce((acc, { key, value }) => {
      if (key) acc[key] = value;
      return acc;
    }, {} as Record<string, string>);

    updateNodeData(node.id, {
      label,
      description,
      integration: integration || undefined,
      action: action || undefined,
      timeout: timeout || undefined,
      params: Object.keys(paramsObj).length ? paramsObj : undefined,
    });
  };

  const nodeType = node.type || node.data.nodeType as string;
  const isTrigger = nodeType?.startsWith('trigger_');
  const isControl = nodeType?.startsWith('control_');
  const isAI = nodeType?.startsWith('ai_');

  const getTypeLabel = () => {
    if (isTrigger) return 'Trigger';
    if (isControl) return 'Control';
    if (isAI) return 'AI';
    if (nodeType?.startsWith('action_')) return 'Action';
    if (nodeType?.startsWith('integration_')) return 'Integration';
    return 'Node';
  };

  return (
    <div className="w-72 bg-white border-l border-gray-200 flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <div>
          <div className="text-[10px] text-gray-400 uppercase tracking-widest">{getTypeLabel()} Properties</div>
          <div className="text-sm font-semibold text-white mt-0.5 truncate max-w-[200px]">{label || 'Unnamed Node'}</div>
        </div>
        <button onClick={onClose} className="p-1.5 hover:bg-gray-50 rounded-lg text-gray-400 hover:text-gray-900 transition-colors">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Label */}
        <div>
          <label className="text-xs text-gray-500 block mb-1.5">Label</label>
          <input
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-orbayx-500 transition-all"
            placeholder="Node label"
          />
        </div>

        {/* Description */}
        <div>
          <label className="text-xs text-gray-500 block mb-1.5">Description</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-orbayx-500 transition-all resize-none"
            placeholder="Optional description"
          />
        </div>

        {/* Integration (for action nodes) */}
        {!isTrigger && !isControl && (
          <>
            <div>
              <label className="text-xs text-gray-500 block mb-1.5">Integration</label>
              <input
                type="text"
                value={integration}
                onChange={(e) => setIntegration(e.target.value)}
                className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-orbayx-500 transition-all"
                placeholder="e.g. active_directory"
              />
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1.5">Action</label>
              <input
                type="text"
                value={action}
                onChange={(e) => setAction(e.target.value)}
                className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-orbayx-500 transition-all"
                placeholder="e.g. user.create"
              />
            </div>
          </>
        )}

        {/* Timeout */}
        <div>
          <label className="text-xs text-gray-500 block mb-1.5">Timeout (seconds)</label>
          <input
            type="number"
            value={timeout}
            onChange={(e) => setTimeout_(parseInt(e.target.value) || 60)}
            className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-orbayx-500 transition-all"
          />
        </div>

        {/* Parameters */}
        {!isTrigger && !isControl && (
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-xs text-gray-500">Parameters</label>
              <button
                onClick={() => setParams((p) => [...p, { key: '', value: '' }])}
                className="flex items-center gap-1 text-xs text-orbayx-600 hover:text-orbayx-700 transition-colors"
              >
                <Plus className="w-3 h-3" /> Add
              </button>
            </div>
            <div className="space-y-2">
              {params.map((param, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input
                    type="text"
                    value={param.key}
                    onChange={(e) => setParams((p) => p.map((pp, j) => j === i ? { ...pp, key: e.target.value } : pp))}
                    placeholder="Key"
                    className="flex-1 bg-gray-50 border border-gray-200 rounded px-2 py-1.5 text-xs text-white focus:outline-none focus:border-orbayx-500 transition-all"
                  />
                  <input
                    type="text"
                    value={param.value}
                    onChange={(e) => setParams((p) => p.map((pp, j) => j === i ? { ...pp, value: e.target.value } : pp))}
                    placeholder="Value or {{var}}"
                    className="flex-1 bg-gray-50 border border-gray-200 rounded px-2 py-1.5 text-xs text-white focus:outline-none focus:border-orbayx-500 transition-all"
                  />
                  <button
                    onClick={() => setParams((p) => p.filter((_, j) => j !== i))}
                    className="text-gray-400 hover:text-red-400 transition-colors"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
            {params.length === 0 && (
              <div className="text-xs text-gray-400 italic">No parameters. Use {'{{variable}}'} syntax for dynamic values.</div>
            )}
          </div>
        )}

        {/* Variable interpolation hint */}
        <div className="flex items-start gap-2 p-3 bg-orbayx-500/5 border border-orbayx-500/15 rounded-lg">
          <Info className="w-3.5 h-3.5 text-orbayx-600 mt-0.5 flex-shrink-0" />
          <div className="text-[10px] text-gray-500 leading-relaxed">
            Use <code className="text-orbayx-600">{'{{variable_name}}'}</code> to reference workflow variables or previous step outputs.
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="p-4 border-t border-gray-200">
        <button
          onClick={handleSave}
          className="w-full px-4 py-2 bg-orbayx-600 hover:bg-orbayx-500 rounded-lg text-sm text-white font-medium transition-all"
        >
          Apply Changes
        </button>
      </div>
    </div>
  );
}
